"use strict"

import CancelSubscriptionDialogTemplate from "./cancel-subscription-dialog.html"
import {
  SubscriptionPlans,
  SubscriptionTiers,
} from "../../model/domain/subscription"
import GoogleFormService from "../../services/google-forms/google-form.service"

/**
 * Dialog to confirm canceling a classkick pro subscription
 */
export default class CancelSubscriptionDialogController {
  /**
   * @param {GoogleFormService} GoogleFormService
   */
  constructor($mdDialog, GoogleFormService, user, subscription, contract) {
    "ngInject"

    this.$mdDialog = $mdDialog

    /** @type {GoogleFormService} */
    this._googleFormService = GoogleFormService

    /** @type {User} */
    this._user = user
    /** @type {Subscription} */
    this._subscription = subscription
    /** @type {Contract} */
    this._contract = contract

    this._cancelReasons = [
      {
        option: "Using a different product",
        message:
          "Which product are you switching to and what features attracted you?",
        needsInput: true,
        placeholder: "Enter text here",
      },
      {
        option: "I can't afford it",
        message:
          "Contact <a href='mailto:support@classkick.com'>support@classkick.com</a> for a 50% discount for the rest of the school year",
        needsInput: false,
      },
      {
        option: "My school upgraded to Pro",
        message: "That's great! Enjoy Classkick Pro through your school",
        needsInput: false,
      },
      {
        option: "I'm not using it enough",
        message:
          "We offer usage-based plans. Contact <a href='mailto:support@classkick.com'>support@classkick.com</a> to find a better fit",
        needsInput: false,
      },
      {
        option: "Missing features I need",
        message:
          "Please list the features below - it helps us build a better product and win you back",
        needsInput: true,
        placeholder: "Enter text here",
      },
      {
        option: "Technical issues or bugs",
        message:
          "Please describe the issues below or email <a href='mailto:support@classkick.com'>support@classkick.com</a> so we can help",
        needsInput: true,
        placeholder: "Enter text here",
      },
      {
        option: "If you added (or fixed) ONE thing, I'd keep my subscription!",
        message: "Please tell us what would keep you subscribed",
        needsInput: true,
        placeholder: "Enter text here",
      },
      {
        option: "Purchased by mistake",
        message:
          "Got it. Contact <a href='mailto:support@classkick.com'>support@classkick.com</a> if you require assistance",
        needsInput: false,
      },
    ]

    this._displayedCancelReasons = this._shuffleArray([...this._cancelReasons])

    this._selectedReasons = {}
    this._reasonInputs = {}

    /** @type {string} */
    this._customCancelReason = ""

    this._confirmCancel = false
  }

  /**
   * Randomize array order to avoid selection bias
   * @param {Array} array - Array to randomize
   * @return {Array} - Randomized array
   */
  _shuffleArray(array) {
    return array.sort(() => Math.random() - 0.5)
  }

  get displayedCancelReasons() {
    return this._displayedCancelReasons
  }

  /**
   * Get array of just the option strings for display
   * @return {string[]}
   */
  get displayedCancelOptions() {
    return this._displayedCancelReasons.map((reason) => reason.option)
  }

  get selectedReasons() {
    return this._selectedReasons
  }

  set selectedReasons(value) {
    this._selectedReasons = value
  }

  get reasonInputs() {
    return this._reasonInputs
  }

  set reasonInputs(value) {
    this._reasonInputs = value
  }

  hasSelectedReasons() {
    return Object.values(this._selectedReasons).some((selected) => selected)
  }

  /**
   * Get messages for currently selected reasons
   * @return {string[]}
   */
  getMessagesForSelections() {
    return this._displayedCancelReasons
      .filter((reason) => this._selectedReasons[reason.option])
      .map((reason) => reason.message)
  }

  /**
   * @return {string}
   */
  get customCancelReason() {
    return this._customCancelReason
  }

  /**
   * @param value {string}
   */
  set customCancelReason(value) {
    this._customCancelReason = value
  }

  /**
   * @return {boolean}
   */
  get confirmCancel() {
    return this._confirmCancel
  }

  /**
   * @param value {boolean}
   */
  set confirmCancel(value) {
    this._confirmCancel = value
  }

  /**
   * @return {Contract}
   */
  get contract() {
    return this._contract
  }

  /**
   * Get the final cancel reason to send
   * @return {string}
   */
  _getFinalCancelReason() {
    const selectedOptions = Object.keys(this._selectedReasons).filter(
      (key) => this._selectedReasons[key],
    )

    let reason = selectedOptions
      .map((option) => {
        const input = this._reasonInputs[option]
        if (input && input.trim()) {
          return `${option}: ${input}`
        }
        return option
      })
      .join("; ")

    if (this._customCancelReason && this._customCancelReason.trim()) {
      reason += reason
        ? " - Additional comments: " + this._customCancelReason
        : this._customCancelReason
    }

    return reason
  }

  acknowledgeCancellationText() {
    const isLegacyPlan = !SubscriptionTiers.some(
      (tier) => tier.id === this._subscription.subscriptionPlan,
    )

    return isLegacyPlan
      ? "I acknowledge that canceling this legacy plan means permanently losing my plan with unlimited access privilege. All new and future plans are usage based"
      : "I acknowledge that I want to cancel this plan"
  }

  /**
   * @param $mdDialog
   * @param user {User}
   * @param subscription {Subscription}
   * @param contract {Contract}
   * @returns {Promise.<string>}
   */
  static show($mdDialog, user, subscription, contract) {
    return $mdDialog.show({
      controller: CancelSubscriptionDialogController,
      template: CancelSubscriptionDialogTemplate,
      controllerAs: "ctrl",
      clickOutsideToClose: true,
      escapeToClose: true,
      locals: {
        user,
        subscription,
        contract,
      },
      focusOnOpen: false,
    })
  }

  cancel() {
    this.$mdDialog.cancel()
  }

  cancelSubscription() {
    const finalReason = this._getFinalCancelReason()
    this._googleFormService.sendCancelSubscriptionReason(
      this._user,
      this._subscription,
      finalReason,
    )
    this.$mdDialog.hide()
  }
}
